/**
 * Console SQL for the record open in a details drawer.
 *
 * Every query reads the application tables the console
 * reader role is granted, so what a drawer offers is exactly what `/query`
 * can run. Identifiers come from the API, and single quotes are still escaped
 * because the text ends up inside SQL literals.
 */
export type RecordQuery = { title: string; sql: string };
const literal = (value: string) => `'${value.replaceAll("'", "''")}'`;

export function runQueries(run: {
  workflowId: string;
  workflowKey: string;
  sourceId?: string | null;
}): RecordQuery[] {
  // The ledger row is addressed by its Temporal workflow ID.
  const workflowId = literal(run.workflowId);
  const runId = `(SELECT id FROM ops.runs WHERE workflow_id = ${workflowId})`;
  return [
    {
      title: "This run",
      sql: `SELECT "trigger" AS triggered_by, fetched, created AS added, removed, baseline,
       created_at AS requested_at, applied_at, last_ingested_at, workflow_id
FROM ops.runs
WHERE workflow_id = ${workflowId}`,
    },
    {
      title: "Activity this run collected",
      sql: `SELECT a.observed_at, a.type_key, a.title, i.handle AS actor, r.title AS scope, a.url,
       (a.origin <> 'event')
FROM app.signal_observations s
JOIN app.signals a ON a.id = s.signal_id
LEFT JOIN app.identities i ON i.id = a.identity_id
LEFT JOIN app.resources r ON r.id = a.scope_resource_id
WHERE s.run_id = ${runId}
ORDER BY a.observed_at DESC
LIMIT 500`,
    },
    {
      title: "Signal breakdown for this run",
      sql: `SELECT a.type_key, count(*) AS records, count(*) FILTER (WHERE (a.origin <> 'event')) AS baseline,
       count(*) FILTER (WHERE NOT (a.origin <> 'event')) AS new_records
FROM app.signal_observations s
JOIN app.signals a ON a.id = s.signal_id
WHERE s.run_id = ${runId}
GROUP BY 1
ORDER BY records DESC`,
    },
    {
      title: "People this run surfaced",
      sql: `SELECT DISTINCT p.display_name, i.handle, i.url, p.first_seen_at
FROM app.signal_observations s
JOIN app.signals a ON a.id = s.signal_id
JOIN app.identities i ON i.id = a.identity_id
LEFT JOIN app.people p ON p.id = i.person_id
WHERE s.run_id = ${runId}
ORDER BY p.first_seen_at DESC NULLS LAST`,
    },
    {
      title: "Raw ingestion batches",
      sql: `SELECT batch_id, received_at, result
FROM ops.ingestion_receipts
WHERE run_id = ${runId}
ORDER BY received_at`,
    },
    ...(run.sourceId
      ? [
          {
            title: "Recent runs for this source",
            sql: `SELECT created_at AS requested_at, "trigger" AS triggered_by, fetched, created AS added, removed,
       baseline, applied_at, workflow_id
FROM ops.runs
WHERE workflow_key = ${literal(run.workflowKey)} AND source_id = ${literal(run.sourceId)}
ORDER BY created_at DESC
LIMIT 50`,
          },
        ]
      : []),
  ];
}

export function activityQueries(activityId: string): RecordQuery[] {
  const id = literal(activityId.replace(/^observation:/, ""));
  return [
    {
      title: "This activity",
      sql: `SELECT a.type_key, a.title, a.body, a.url, a.occurred_at, a.observed_at, (a.origin <> 'event'),
       i.handle AS actor, r.title AS scope
FROM app.signals a
LEFT JOIN app.identities i ON i.id = a.identity_id
LEFT JOIN app.resources r ON r.id = a.scope_resource_id
WHERE a.id = ${id}`,
    },
    {
      title: "Runs that collected it",
      sql: `SELECT w.created_at AS requested_at, w.workflow_key, w.workflow_id, s.observed_at
FROM app.signal_observations s
JOIN ops.runs w ON w.id = s.run_id
WHERE s.signal_id = ${id}
ORDER BY s.observed_at DESC`,
    },
    {
      title: "Deliveries of this record",
      sql: `SELECT a.created_at, a.destination_name, a.state, a.attempts, a.error
FROM app.actions a
JOIN app.action_signals s ON s.action_id = a.id
WHERE s.signal_id = ${id}
ORDER BY a.created_at DESC`,
    },
  ];
}

export function personQueries(personId: string): RecordQuery[] {
  const id = literal(personId),
    key = literal(`person:${personId}`);
  return [
    {
      title: "This person",
      sql: `SELECT p.display_name, p.first_name, p.last_name, c.name AS company, CASE WHEN p.overrides ? 'location' THEN p.overrides->>'location' ELSE l.name END AS location,
       p.first_seen_at, p.last_seen_at
FROM app.people p
LEFT JOIN app.companies c ON c.id = p.company_id
LEFT JOIN app.locations l ON l.id = p.location_id
WHERE p.id = ${id}`,
    },
    {
      title: "Identities and profiles",
      sql: `SELECT provider, account_kind, handle, url, first_seen_at, last_seen_at, profile
FROM app.identities
WHERE person_id = ${id}
ORDER BY first_seen_at`,
    },
    {
      title: "Everything collected about them",
      sql: `SELECT a.observed_at, a.type_key, a.title, r.title AS scope, a.url, (a.origin <> 'event')
FROM app.signals a
JOIN app.identities i ON i.id = a.identity_id
LEFT JOIN app.resources r ON r.id = a.scope_resource_id
WHERE i.person_id = ${id}
ORDER BY a.observed_at DESC
LIMIT 500`,
    },
    {
      title: "Review and delivery history",
      sql: `SELECT h.created_at, h.action, h.previous_state, h.state, u.name AS actor
FROM app.review_history h
LEFT JOIN app.users u ON u.id = h.actor_user_id
WHERE h.key = ${key}
ORDER BY h.created_at DESC`,
    },
  ];
}

export function companyQueries(companyId: string): RecordQuery[] {
  const id = literal(companyId);
  return [
    {
      title: "People at this company",
      sql: `SELECT p.display_name, i.handle, i.url, p.first_seen_at, p.last_seen_at
FROM app.people p
LEFT JOIN app.identities i ON i.id = p.primary_identity_id
WHERE p.company_id = ${id}
ORDER BY p.last_seen_at DESC`,
    },
    {
      title: "Activity from this company",
      sql: `SELECT a.observed_at, a.type_key, p.display_name, a.title, r.title AS scope, a.url
FROM app.signals a
JOIN app.identities i ON i.id = a.identity_id
JOIN app.people p ON p.id = i.person_id
LEFT JOIN app.resources r ON r.id = a.scope_resource_id
WHERE p.company_id = ${id}
ORDER BY a.observed_at DESC
LIMIT 500`,
    },
  ];
}

export function deliveryQueries(delivery: { id: string; recordKey?: string }): RecordQuery[] {
  const id = literal(delivery.id);
  return [
    {
      title: "This delivery",
      sql: `SELECT created_at, destination_name, 'person:' || person_id AS record_key, state,
       "trigger" AS triggered_by, attempts, error, completed_at, payload
FROM app.actions
WHERE id = ${id}`,
    },
    {
      title: "Attempts",
      sql: `SELECT attempt, state AS status, http_status, error, duration_ms, started_at, finished_at
FROM app.action_attempts
WHERE action_id = ${id}
ORDER BY attempt`,
    },
    ...(delivery.recordKey
      ? [
          {
            title: "Every delivery of this record",
            sql: `SELECT a.created_at, a.destination_name, a.state, a.attempts, a.error
FROM app.actions a
WHERE 'person:' || a.person_id = ${literal(delivery.recordKey)}
   OR EXISTS (SELECT 1 FROM app.action_signals s WHERE s.action_id = a.id
              AND 'observation:' || s.signal_id = ${literal(delivery.recordKey)})
ORDER BY created_at DESC`,
          },
        ]
      : []),
  ];
}
